import React, { useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DigitalTwinTableSearch from "../../../Components/DigitalTwinTableSearch";
import SeverityOfFaultsChartView from "./SeverityOfFaultsChartView";
import AutoResolvedChartView from "./AutoResolvedChartView";
import ChartDetailsSettingsSidebar from "./ChartDetailsSettingsSidebar";
import { CHART_TYPES, CHART_CONFIG } from "./chartConfig";
import ChartDetailsTableSection from "./ChartDetailsTableSection";
import { useCanUseGlobalAdvancedFilter } from "../../../Helper/Permissions/GlobalAdvancedFilter";
import AppCache from "../../../Helper/Common/AppCache";
import { useSearchableFields, searchFieldsKey } from "../../../Hooks/usePagesContent";

const DEFAULT_SEARCHABLE_FIELDS = [
  {
    key: "licensePlateNo",
    title: "Bus Number",
  },
  {
    key: "deviceID",
    title: "device_id",
  },
  {
    key: "vin",
    title: "Chassis Number",
  },
];

const ChartDetailsView = ({ chartType, onBack, startDate: initialStartDate, endDate: initialEndDate, initialGranularity, allFleets, allOnboardingLocationsHierarchy, initialHierarchicalLocation, initialFleetIds, onboardingLocations, allOnboardingLocations, initialAlertCategories, initialOperationalStatus, onGlobalFiltersChange }) => {
  const searchableFields = useSearchableFields(DEFAULT_SEARCHABLE_FIELDS);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [granularity, setGranularity] = useState(initialGranularity || "Weekly");
  const [appliedFilters, setAppliedFilters] = useState([]);
  // Same gate the sidebar uses to decide whether to render GlobalFilter at all.
  const canUseGlobalAdvancedFilter = useCanUseGlobalAdvancedFilter();
  const globalFilterConfig =
    AppCache.getItem("globalFilter")?.find((f) => f.type === "unifiedGlobalFilter") || null;

  const [localHierarchicalLocation, setLocalHierarchicalLocation] = useState(initialHierarchicalLocation);
  // Seeded from the page scope when the customer control is available, so this
  // view honours the customers its sidebar already shows as applied. Left null
  // otherwise: this module sends no fleetIDs header, and an org with no customer
  // control must keep every request exactly as it was.
  //
  // Seeding rather than starting null is what makes Reset idempotent. With null,
  // the sidebar's reset ref held initialFleetIds while the requests held nothing,
  // so an untouched Reset - or a location-only Apply, which forwards the parent's
  // own fleet value back - switched the header on and silently dropped every row
  // whose fleet is unmapped.
  const [localFleetIds, setLocalFleetIds] = useState(
    canUseGlobalAdvancedFilter && globalFilterConfig ? (initialFleetIds || []) : null
  );
  const [localAlertCategories, setLocalAlertCategories] = useState(initialAlertCategories || []);
  const [localOperationalStatus, setLocalOperationalStatus] = useState(initialOperationalStatus);
  const [searchTerm, setSearchTerm] = useState({
    selectedDevices: [],
    selectedSearchColumn: null,
  });

  const config = CHART_CONFIG[chartType] || CHART_CONFIG[CHART_TYPES.SEVERITY];


  const handleFiltersChange = useCallback((filters) => {
    setAppliedFilters(filters);
  }, []);


  const handleHierarchicalLocationChange = useCallback((location) => {
    setLocalHierarchicalLocation(location);
  }, []);


  const handleFleetIdsChange = useCallback((fleetIds) => {
    setLocalFleetIds(fleetIds);
  }, []);


  const handleAlertCategoriesChange = useCallback((categories) => {
    setLocalAlertCategories(categories);
  }, []);

  const handleOperationalStatusChange = useCallback((status) => {
    setLocalOperationalStatus(status);
    // Propagate changes back to global filters
    if (onGlobalFiltersChange) {
      onGlobalFiltersChange({ operationalStatus: status });
    }
  }, [onGlobalFiltersChange]);


  const handleSearch = (selectedDevices, selectedSearchColumn) => {
    setSearchTerm({
      selectedDevices: selectedDevices,
      selectedSearchColumn: selectedSearchColumn,
    });
  };


  const renderChart = () => {
    switch (chartType) {
      case CHART_TYPES.AUTO_RESOLVED:
        return (
          <AutoResolvedChartView
            isSidebarExpanded={isSidebarExpanded}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            filters={appliedFilters}
            hierarchicalLocation={localHierarchicalLocation}
            fleetIds={localFleetIds}
            onboardingLocations={onboardingLocations}
            allOnboardingLocations={allOnboardingLocations}
            alertCategories={localAlertCategories}
            operationalStatus={localOperationalStatus}
            searchTerm={searchTerm}
            inDetailsView={true}
          />
        );
      case CHART_TYPES.SEVERITY:
      default:
        return (
          <SeverityOfFaultsChartView
            isSidebarExpanded={isSidebarExpanded}
            startDate={startDate}
            endDate={endDate}
            granularity={granularity}
            filters={appliedFilters}
            hierarchicalLocation={localHierarchicalLocation}
            fleetIds={localFleetIds}
            onboardingLocations={onboardingLocations}
            allOnboardingLocations={allOnboardingLocations}
            alertCategories={localAlertCategories}
            operationalStatus={localOperationalStatus}
            searchTerm={searchTerm}
            inDetailsView={true}
          />
        );
    }
  };

  return (
    <Box sx={{ display: "flex", gap: 3, height: "100%", position: "relative" }}>
      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          width: isSidebarExpanded ? "calc(100% - 320px)" : "100%",
          minWidth: 0,
          transition: "width 0.3s ease-in-out",
        }}
      >
        {/* Top Bar with Back Button and Search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Left side - Back button */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              "&:hover": {
                "& .MuiTypography-root": {
                  color: "#6D72F6 !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "#6D72F6",
                },
              },
            }}
            onClick={onBack}
          >
            <ChevronLeftIcon sx={{ fontSize: 20, color: "#2E2E2E" }} />
            <Typography
              variant="fw500s14lh24c2E2E2E"
            >
              Back
            </Typography>
          </Box>
        </Box>

        <DigitalTwinTableSearch
          onSearch={handleSearch}
          containerWidth="80%"
          key={searchFieldsKey(searchableFields)}
          searchableFields={searchableFields}
        />

        {/* Chart Section */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            padding: "24px 22px",
            border: "1px solid #E6E6E6",
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
              {config.title}
            </Typography>
          </Box>
          {renderChart()}
        </Box>

        {/* Vehicle List Table */}
        <ChartDetailsTableSection
          title="Vehicle List"
          config={config}
          emptyMessage="No vehicles found"
          startDate={startDate}
          endDate={endDate}
          chartType={chartType}
          appliedFilters={appliedFilters}
          searchTerm={searchTerm}
          hierarchicalLocation={localHierarchicalLocation}
          fleetIds={localFleetIds}
            onboardingLocations={onboardingLocations}
            allOnboardingLocations={allOnboardingLocations}
          alertCategories={localAlertCategories}
          operationalStatus={localOperationalStatus}
        />
      </Box>

      {/* Settings Sidebar */}
      <ChartDetailsSettingsSidebar
        isExpanded={isSidebarExpanded}
        onToggle={() => setIsSidebarExpanded(!isSidebarExpanded)}
        chartType={chartType}
        startDate={startDate}
        endDate={endDate}
        onDateChange={({ startDate, endDate }) => {
          setStartDate(startDate);
          setEndDate(endDate);
        }}
        granularity={granularity}
        onGranularityChange={setGranularity}
        allFleets={allFleets || []}
        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
        selectedFleetIds={localFleetIds || []}
        selectedHierarchicalLocation={localHierarchicalLocation}
        initialAlertCategories={localAlertCategories}
        initialOperationalStatus={localOperationalStatus}
        onFiltersChange={handleFiltersChange}
        onHierarchicalLocationChange={handleHierarchicalLocationChange}
        onFleetIdsChange={handleFleetIdsChange}
        onAlertCategoriesChange={handleAlertCategoriesChange}
        onOperationalStatusChange={handleOperationalStatusChange}
      />
    </Box>
  );
};

export default ChartDetailsView;
